import React, { useState } from "react";
import { Clock, Check, X, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import SLABubble from "./SLABubble";

const PRESET_LABELS = {
  "24h": "24 hours",
  "48h": "48 hours",
  "72h": "72 hours",
  "custom": "Custom",
};

const PRESETS = ["24h", "48h", "72h", "custom"];

/**
 * Follow-up reminder control. Embedded in SubmissionDetailPage right column.
 *
 * Props:
 *   doc        — submission doc with follow_up_due, follow_up_preset, follow_up_completed_at, sla_state, status
 *   onSet      — async (body) => void   POST /follow-up
 *   onComplete — async () => void       POST /follow-up/complete
 *   onCancel   — async () => void       DELETE /follow-up
 *   disabled   — read-only flag
 */
export default function FollowUpControl({ doc, onSet, onComplete, onCancel, disabled }) {
  const [customDate, setCustomDate] = useState("");
  const [busy, setBusy] = useState(false);

  const isClosed = ["won", "lost", "spam"].includes(doc.status);
  const hasActive = doc.follow_up_due && !doc.follow_up_completed_at;

  const handlePreset = async (preset) => {
    if (disabled || busy || isClosed) return;
    setBusy(true);
    try {
      if (preset === "custom") {
        // handled by separate button below
      } else {
        await onSet({ preset });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCustom = async () => {
    if (!customDate || disabled || busy || isClosed) return;
    setBusy(true);
    try {
      // Convert the datetime-local string to ISO with timezone
      const iso = new Date(customDate).toISOString();
      await onSet({ preset: "custom", due_date: iso });
      setCustomDate("");
    } finally {
      setBusy(false);
    }
  };

  const handleComplete = async () => {
    if (disabled || busy) return;
    setBusy(true);
    try {
      await onComplete();
    } finally {
      setBusy(false);
    }
  };

  const handleCancelActive = async () => {
    if (disabled || busy) return;
    setBusy(true);
    try {
      await onCancel();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="follow-up-control">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-slate-900 inline-flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4" /> Follow-up
        </h3>
        {doc.sla_state && (
          <SLABubble state={doc.sla_state} dueIso={doc.follow_up_due} testid="follow-up-sla-bubble" />
        )}
      </div>

      {hasActive ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
          <div className="text-xs text-slate-600">
            Due:{" "}
            <span className="font-medium text-slate-900" data-testid="follow-up-due-text">
              {new Date(doc.follow_up_due).toLocaleString(undefined, {
                month: "short", day: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}
            </span>
            {doc.follow_up_preset && doc.follow_up_preset !== "custom" && (
              <span className="text-slate-500"> ({PRESET_LABELS[doc.follow_up_preset]})</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleComplete}
              disabled={disabled || busy}
              data-testid="follow-up-complete-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Check className="h-3.5 w-3.5 mr-1.5" /> Mark complete
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleCancelActive}
              disabled={disabled || busy}
              data-testid="follow-up-cancel-btn"
              className="text-slate-600 hover:text-slate-900"
            >
              <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {isClosed ? (
            <p className="text-xs text-slate-500 italic">
              Follow-ups are disabled on closed leads.
            </p>
          ) : (
            <>
              <p className="text-xs text-slate-600">Set a follow-up reminder:</p>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.filter((p) => p !== "custom").map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handlePreset(p)}
                    disabled={disabled || busy}
                    data-testid={`follow-up-preset-${p}`}
                    className="px-2.5 py-1 rounded-md border border-slate-200 text-xs font-medium text-slate-700 hover:border-blue-300 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {PRESET_LABELS[p]}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                <input
                  type="datetime-local"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  disabled={disabled || busy}
                  data-testid="follow-up-custom-input"
                  className="text-xs border border-slate-200 rounded-md px-2 py-1 flex-1 disabled:opacity-50"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCustom}
                  disabled={!customDate || disabled || busy}
                  data-testid="follow-up-custom-btn"
                  className="text-xs h-7"
                >
                  Set
                </Button>
              </div>
            </>
          )}
          {doc.follow_up_completed_at && (
            <p className="text-[11px] text-slate-500" data-testid="follow-up-last-completed">
              Last completed {new Date(doc.follow_up_completed_at).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
